import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useTheme } from "../contexts/ThemeContext";
import { Logo } from "../components/common/Logo";

export const PrivacyPage: React.FC = () => {
  const { isDark } = useTheme();

  return (
    <div className={`min-h-screen ${isDark ? 'bg-black text-white' : 'bg-white text-black'} transition-colors duration-300`}>
      {/* Header */}
      <div className={`border-b ${isDark ? 'border-white/10' : 'border-black/10'}`}>
        <div className="max-w-4xl mx-auto px-4 py-6">
          <Link
            to="/"
            className={`inline-flex items-center gap-2 ${isDark ? 'text-white/60 hover:text-white' : 'text-black/60 hover:text-black'} transition-colors`}
          >
            <ArrowLeft size={20} />
            Back to Home
          </Link>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        <h1 className={`text-4xl font-black ${isDark ? 'text-white' : 'text-black'} mb-2`}>Privacy Policy</h1>
        <p className={`${isDark ? 'text-white/60' : 'text-black/60'} mb-12`}>Last updated: November 27, 2024</p>

        <div className={`space-y-8 ${isDark ? 'text-white/80' : 'text-black/80'}`}>
          <section>
            <h2 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-black'} mb-4`}>1. Introduction</h2>
            <p className="leading-relaxed">
              ParlayGround ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy
              explains how we collect, use, disclose, and safeguard your information when you use our mobile
              application and website (collectively, the "Service"). Please read this policy carefully.
            </p>
          </section>

          <section>
            <h2 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-black'} mb-4`}>2. Information We Collect</h2>

            <h3 className={`text-lg font-semibold ${isDark ? 'text-white/90' : 'text-black/90'} mt-6 mb-3`}>Personal Information</h3>
            <p className="leading-relaxed mb-4">When you create an account, we may collect:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Email address</li>
              <li>Username and display name</li>
              <li>Profile photo (if provided)</li>
              <li>Authentication data from third-party providers (Google, Apple)</li>
            </ul>

            <h3 className={`text-lg font-semibold ${isDark ? 'text-white/90' : 'text-black/90'} mt-6 mb-3`}>Usage Information</h3>
            <p className="leading-relaxed mb-4">We automatically collect:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Device information (type, operating system, unique identifiers)</li>
              <li>Log data (IP address, access times, pages viewed)</li>
              <li>In-app activity (rooms joined, messages sent, GT transactions)</li>
              <li>Performance and crash data</li>
            </ul>

            <h3 className={`text-lg font-semibold ${isDark ? 'text-white/90' : 'text-black/90'} mt-6 mb-3`}>Payment Information</h3>
            <p className="leading-relaxed">
              If you purchase GroundTokens, payment processing is handled by Stripe. We do not store your
              full credit card number. We may receive transaction confirmations and partial card details
              (last 4 digits, expiration date) for your records.
            </p>
          </section>

          <section>
            <h2 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-black'} mb-4`}>3. How We Use Your Information</h2>
            <p className="leading-relaxed mb-4">We use collected information to:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Provide, maintain, and improve our Service</li>
              <li>Create and manage your account</li>
              <li>Process transactions and send related information</li>
              <li>Send notifications about activity, updates, and promotions</li>
              <li>Respond to your comments, questions, and support requests</li>
              <li>Monitor and analyze usage patterns and trends</li>
              <li>Detect, prevent, and address fraud and abuse</li>
              <li>Enforce our Terms of Service and community guidelines</li>
            </ul>
          </section>

          <section>
            <h2 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-black'} mb-4`}>4. Information Sharing</h2>
            <p className="leading-relaxed mb-4">We may share your information with:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li><strong>Other Users:</strong> Your public profile, username, and activity may be visible to other users</li>
              <li><strong>Service Providers:</strong> Third parties that help us operate our Service (hosting, analytics, payment processing)</li>
              <li><strong>Legal Requirements:</strong> When required by law or to protect our rights and safety</li>
              <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
            </ul>
            <p className="leading-relaxed mt-4">
              We do not sell your personal information to third parties for marketing purposes.
            </p>
          </section>

          <section>
            <h2 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-black'} mb-4`}>5. Data Security</h2>
            <p className="leading-relaxed">
              We implement appropriate technical and organizational measures to protect your information,
              including encryption, secure servers, and access controls. However, no method of transmission
              over the Internet is 100% secure, and we cannot guarantee absolute security.
            </p>
          </section>

          <section>
            <h2 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-black'} mb-4`}>6. Data Retention</h2>
            <p className="leading-relaxed">
              We retain your information for as long as your account is active or as needed to provide you
              services. We may retain certain information as required by law or for legitimate business
              purposes. You can request deletion of your account and associated data at any time.
            </p>
          </section>

          <section>
            <h2 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-black'} mb-4`}>7. Your Rights and Choices</h2>
            <p className="leading-relaxed mb-4">You have the right to:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Access and receive a copy of your personal data</li>
              <li>Correct inaccurate or incomplete information</li>
              <li>Delete your account and personal data</li>
              <li>Opt-out of marketing communications</li>
              <li>Disable push notifications through your device settings</li>
            </ul>
            <p className="leading-relaxed mt-4">
              To exercise these rights, contact us at{" "}
              <a href="mailto:privacy@parlayground.com" className="text-primary hover:underline">
                privacy@parlayground.com
              </a>
            </p>
          </section>

          <section>
            <h2 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-black'} mb-4`}>8. Third-Party Services</h2>
            <p className="leading-relaxed mb-4">Our Service integrates with third-party services including:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li><strong>Firebase:</strong> Authentication and database services (Google)</li>
              <li><strong>Stripe:</strong> Payment processing</li>
              <li><strong>Google/Apple Sign-In:</strong> Authentication providers</li>
            </ul>
            <p className="leading-relaxed mt-4">
              These services have their own privacy policies, and we encourage you to review them.
            </p>
          </section>

          <section>
            <h2 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-black'} mb-4`}>9. Children's Privacy</h2>
            <p className="leading-relaxed">
              Our Service is not intended for users under 18 years of age. We do not knowingly collect
              personal information from children. If we learn we have collected information from a child
              under 18, we will delete that information promptly.
            </p>
          </section>

          <section>
            <h2 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-black'} mb-4`}>10. International Users</h2>
            <p className="leading-relaxed">
              Our Service is operated in the United States. If you are accessing our Service from outside
              the United States, please be aware that your information may be transferred to, stored, and
              processed in the United States where our servers are located.
            </p>
          </section>

          <section>
            <h2 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-black'} mb-4`}>11. Changes to This Policy</h2>
            <p className="leading-relaxed">
              We may update this Privacy Policy from time to time. We will notify you of any changes by
              posting the new Privacy Policy on this page and updating the "Last updated" date. Your
              continued use of the Service after any changes indicates your acceptance of the updated policy.
            </p>
          </section>

          <section>
            <h2 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-black'} mb-4`}>12. Contact Us</h2>
            <p className="leading-relaxed">
              If you have questions or concerns about this Privacy Policy, please contact us at:
            </p>
            <div className={`mt-4 p-4 ${isDark ? 'bg-white/5' : 'bg-black/5'} rounded-xl`}>
              <p className={`font-semibold ${isDark ? 'text-white' : 'text-black'}`}>ParlayGround</p>
              <p>
                Email:{" "}
                <a href="mailto:privacy@parlayground.com" className="text-primary hover:underline">
                  privacy@parlayground.com
                </a>
              </p>
            </div>
          </section>
        </div>

        {/* Footer */}
        <div className={`mt-16 pt-8 border-t ${isDark ? 'border-white/10' : 'border-black/10'}`}>
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-2">
              <Logo size="sm" showText={true} />
            </Link>
          </div>
          <p className={`${isDark ? 'text-white/40' : 'text-black/40'} text-sm mt-4`}>
            © {new Date().getFullYear()} ParlayGround. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPage;
